<template>
  <v-main>
    <v-container
      id="stripe-payment-container"
      fluid
      tag="section"
    >
      <base-material-card
        icon="mdi-credit-card-outline"
        class="px-5 py-3"
      >
        <div class="page-message">
          <shared-page-messages
            :error-messages="errorMessages"
            :success-messages="successMessages"
            :warning-messages="warningMessages"
          />
        </div>


        <v-card-text v-if="status === 'success' && invoice !== null && paymentResult != null">
          <v-row justify="center">
            <v-col sm="12" md="6" class="text-center">
              <v-icon color="success" size="64">mdi-check-circle-outline</v-icon>
              <p class="text-h6 mt-4">Payment Successful!</p>
              <p class="p-5">Your Payment has been processed successfully.</p>

              <v-row class="text-left mt-10">
                <v-col class="font-weight-bold" cols="6" lg="4" sm="6">
                  Amount Paid:
                </v-col>
                <v-col cols="6" lg="8" sm="6">
                  {{paymentResult.amount | formatCurrencyUSD}}
                </v-col>
              </v-row>
              <v-row class="text-left">
                <v-col class="font-weight-bold" cols="6" lg="4" sm="6">
                  Member:
                </v-col>
                <v-col cols="6" lg="8" sm="6">
                  {{invoice.user.names}}
                </v-col>
              </v-row>

              <v-row class="text-left">
                <v-col class="font-weight-bold" cols="6" lg="4" sm="6">
                  Description:
                </v-col>
                <v-col cols="6" lg="8" sm="6">
                  {{itemDescription}}
                </v-col>
              </v-row>
              <v-row class="text-left">
                <v-col class="font-weight-bold" cols="6" lg="4" sm="6">
                  Transaction ID:
                </v-col>
                <v-col cols="6" lg="8" sm="6">
                  {{paymentResult.paymentId}}
                </v-col>
              </v-row>


              <v-btn color="primary" :to="{ name: 'view_invoices' }" class="mt-10">Go to My Invoices</v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-text v-else-if="status === 'cancelled'">
          <v-row>
            <v-col cols="12" sm="6" class="text-center">
              <v-icon color="warning" size="64">mdi-close-circle-outline</v-icon>
              <p class="text-h6 mt-4">Payment Cancelled</p>
              <p>Your payment was not completed. You can try again below.</p>
              <v-btn color="primary" @click="resetStatus" class="mt-2">Try Again</v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-text v-else-if="invoice">
          <v-form ref="paymentForm" @submit.prevent="startCheckout" lazy-validation>
            <v-row>
              <v-col class="font-weight-bold" cols="12" lg="3" sm="6">
                Invoice #:
              </v-col>
              <v-col cols="3" lg="3" sm="6">
                {{invoiceId}}
              </v-col>
            </v-row>

            <v-row>
              <v-col class="font-weight-bold" cols="12" lg="3" sm="6">
                Payment For:
              </v-col>
              <v-col cols="3" lg="3" sm="6">
                {{itemDescription}}
              </v-col>
            </v-row>

            <v-row>
              <v-col class="font-weight-bold" cols="12" lg="3" sm="6">
                Invoice Date:
              </v-col>
              <v-col cols="3" lg="3" sm="6">
                {{invoice.invoiceDate | formatDate}}
              </v-col>
            </v-row>

            <v-row>
              <v-col class="font-weight-bold" cols="12" lg="3" sm="6">
                Due Date:
              </v-col>
              <v-col cols="3" lg="6" sm="6">
                {{invoice.dueDate | formatDate}}
              </v-col>
            </v-row>

            <v-row>
              <v-col class="font-weight-bold" cols="12" lg="3" sm="6">
                Invoice Amount:
              </v-col>
              <v-col cols="3" lg="3" sm="6">
                {{invoice.total | formatCurrencyUSD}}
              </v-col>
            </v-row>


            <v-row>
              <v-col class="font-weight-bold" cols="12" lg="3" sm="6">
                Balance Due:
              </v-col>
              <v-col cols="3" lg="3" sm="6">
                {{invoice.balance | formatCurrencyUSD}}
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="paymentAmount"
                  label="Payment Amount (USD)"
                  id="paymentAmount"
                  prefix="$"
                  :rules="rules.amount"
                  validate-on-blur
                  required
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="4">
                <v-btn
                  color="primary"
                  dark
                  type="submit"
                  id="checkoutBtn"
                  :loading="loading"
                  :disabled="loading"
                >
                  <v-icon left>mdi-lock-outline</v-icon>
                  Pay ${{ paymentAmount || '0.00' }}
                </v-btn>

                <p class="text-body-2 grey--text py-3">
                  You will be redirected to Stripe's secure checkout to complete your payment.
                </p>

              </v-col>


            </v-row>
          </v-form>
        </v-card-text>

      </base-material-card>
    </v-container>
  </v-main>
</template>

<script>
import api from '@/services/api'
import { mapGetters } from 'vuex'
import SharedPageMessages from '@/views/components/shared/PageMessages.vue'
import validation from '@/utils/validation'

export default {
  name: 'StripePaymentView',

  components: { SharedPageMessages },

  created() {
    this.invoiceId = this.$route.params.invoiceId
    this.userId = this.getUserId
    this.sessionId = this.$route.query.sessionId
    const queryStatus = this.$route.query.status

    if (queryStatus === 'success' || queryStatus === 'cancelled') {
      this.status = queryStatus
    }

    this.fetchInvoice()

    if (this.sessionId !== null && this.status === 'success') {
      this.handlePaymentSuccess()
    }
  },

  computed: {
    ...mapGetters({
      getUserId: 'getUserId'
    })
  },

  watch: {
    invoice(invoice) {
      if (invoice) {
        this.amountDue = invoice.balance.toFixed(2)
        this.itemDescription = this.invoice.items[0].description
        this.paymentAmount = this.amountDue
      }
    }
  },

  methods: {
    fetchInvoice: function () {
      api
          .fetchInvoice(this.userId, this.invoiceId)
          .then(response => {
            this.invoice = response.data
          })
          .catch(err => {
            this.handleError(err)
          })
    },

    handlePaymentSuccess: function () {
      const payload = {
        sessionId: this.sessionId
      }
      api
          .handleStripePaymentResult(this.userId, this.invoiceId, payload)
          .then(response => {
            this.paymentResult = response.data
          })
          .catch(err => {
            this.handleError(err)
          })
    },

    handleError: function (err) {
      let errorMessage = api.parseResponseError(err)
      this.errorMessages.push(errorMessage)
    },

    async startCheckout() {
      if (!this.$refs.paymentForm.validate()) return

      this.loading = true
      this.errorMessages = []

      try {
        const response = await api.createStripeCheckoutSession(this.getUserId, this.invoiceId, {
          amount: Math.round(parseFloat(this.paymentAmount) * 100)
        })

        window.location.href = response.data.checkoutUrl
      } catch (err) {
        this.errorMessages.push(api.parseResponseError(err))
        this.loading = false
      }
    },

    resetStatus() {
      this.status = null
      this.$router.replace({ query: {} })
    }
  },

  data() {
    return {
      loading: false,
      status: null,
      sessionId: null,
      invoiceId: null,
      invoice: null,
      amountDue: null,
      paymentAmount: null,
      itemDescription: null,
      paymentResult: {},
      errorMessages: [],
      successMessages: [],
      warningMessages: [],
      rules: {
        amount: [
          v => !!v || 'Required',
          v => validation.isCurrency(v) || 'Must be a valid dollar amount (e.g. 25.00)',
          v => parseFloat(v) > 1 || 'Must be greater than 1.00',
          v => validation.isLessThanOrEqual(v, this.amountDue) || 'Cannot be more than the balance due',
        ]
      }
    }
  }
}
</script>
